import { createBulletin } from "@rubi/api-generated";

/**
 * The "from a RubiREPORT occurrence" entry point's one API call.
 *
 * It is `PUT /bulletin` — the endpoint every bulletin is created through — with the hand-off token as
 * a parameter of the creation. What comes back is a real draft holding the occurrence's details, its
 * people and vehicles as entities, and its evidence files; so this entry point needs no source of its
 * own in `useBulletinSource`, and the form opens on the edit route and loads a document like any
 * other. Same shape as `pdfImport.ts`, for the same reason (decision 47).
 *
 * A token is an *input to creating a bulletin* rather than a resource with an endpoint of its own,
 * which is why there is no `POST /bulletin/from_report/{token}` here: the thing being created is a
 * bulletin, so it is created where bulletins are created.
 *
 * ## Why a failure is retryable and a success is not
 *
 * The hand-off is consumed **after** the bulletin is committed, so a call that fails leaves the token
 * spendable and trying again is the right advice. A call that succeeds spends it — which is why the
 * caller replaces the history entry rather than pushing one: going Back to a spent token would answer
 * 404 and say nothing useful about the draft that does exist.
 */
export const importBulletinFromReport = async (
  token: string,
): Promise<number> => {
  const created = await createBulletin({ body: { draft_token: token } });
  if (created.error || !created.data) {
    throw created.error ?? new Error("The report hand-off could not be read.");
  }

  // The endpoint answers with the whole bulletin; the id is all this needs, because the form reads
  // the document back through the edit route rather than being handed values.
  return created.data.id;
};
